import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './HomePage.css';

import logoLight from '../assests/logo-light.png';
import logoDark from '../assests/logo-dark.png';
import heroImage1 from '../assests/pic4.jpg';
import heroImage2 from '../assests/pic5.jpg';
import heroImage3 from '../assests/pic6.jpg';



// --- Reusable Animation Components ---
const CountUp = ({ target }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    let current = 0;
                    const increment = target / 100;
                    const updateCount = () => {
                        if (current < target) {
                            current += increment;
                            setCount(Math.ceil(current));
                            requestAnimationFrame(updateCount);
                        } else {
                            setCount(target);
                        }
                    };
                    updateCount();
                    observer.unobserve(ref.current);
                }
            }, { threshold: 0.5 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        }
    }, [target]);

    return <span ref={ref}>{count.toLocaleString('en-US')}</span>;
};

// --- Hero Carousel Component ---
const heroSlides = [
    // --- 2. ADD LOGO AND THEME TO EACH SLIDE ---
    { image: heroImage1, theme: 'dark-theme', logo: logoLight },
    { image: heroImage2, theme: 'dark-theme', logo: logoLight },
    { image: heroImage3, theme: 'light-theme', logo: logoDark }
];


const HeroCarousel = ({ onThemeChange, onLogoChange }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const timeoutRef = useRef(null);

    const resetTimeout = () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };

    useEffect(() => {
        resetTimeout();
        timeoutRef.current = setTimeout(() => setCurrentIndex((prev) => (prev + 1) % heroSlides.length), 5000);
        
        // --- 3. PASS THE CURRENT LOGO AND THEME UP TO THE PARENT ---
        onThemeChange(heroSlides[currentIndex].theme);
        onLogoChange(heroSlides[currentIndex].logo);

        return () => resetTimeout();
    }, [currentIndex, onThemeChange, onLogoChange]);

    const goToSlide = (slideIndex) => setCurrentIndex(slideIndex);
    const goToPrev = () => setCurrentIndex((prev) => (prev === 0 ? heroSlides.length - 1 : prev - 1));
    const goToNext = () => setCurrentIndex((prev) => (prev + 1) % heroSlides.length);

    return (
        <section id="hero" className="hero-section">
            <div className="carousel-container">
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
                        style={{ backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.1)), url('${slide.image}')` }}
                    />
                ))}
            </div>
            <button onClick={goToPrev} className="carousel-arrow left-arrow">&lt;</button>
            <button onClick={goToNext} className="carousel-arrow right-arrow">&gt;</button>
            <div className="hero-content">
                <h1 className="hero-title">The Taste of Home, Delivered.</h1>
                <p className="hero-subtitle">Authentic, homemade Pakistani meals prepared fresh daily for students and professionals.</p>
                <a href="#plans" className="hero-button">Explore Subscription Plans</a>
            </div>
            <div className="carousel-tracker">
                {heroSlides.map((_, slideIndex) => (
                    <button key={slideIndex} onClick={() => goToSlide(slideIndex)} className={`tracker-dot ${currentIndex === slideIndex ? 'active' : ''}`} />
                ))}
            </div>
        </section>
    );
};


// --- Main HomePage Component ---
function HomePage() {
  const [activeSection, setActiveSection] = useState('hero');
  const [heroTheme, setHeroTheme] = useState('dark-theme');
  const [currentLogo, setCurrentLogo] = useState(logoLight); // State for current logo

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => { if (entry.isIntersecting) setActiveSection(entry.target.id); });
    }, { rootMargin: "-50% 0px -50% 0px" });

    const sections = document.querySelectorAll('main > section[id]');
    sections.forEach(section => observer.observe(section));
    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  return (
    <div>
      {/* --- 4. PASS THE CURRENT LOGO TO THE HEADER --- */}
      <Header activeSection={activeSection} heroTheme={heroTheme} logo={currentLogo} />
      <main>
        <HeroCarousel onThemeChange={setHeroTheme} onLogoChange={setCurrentLogo} />
        
        {/* --- RESTORED SECTIONS --- */}
        <section id="promise" className="promise-section">
            <div className="section-container">
                 <div className="promise-card">
                    <span className="promise-icon">🌿</span>
                    <h3 className="promise-title">Organic & Fresh</h3>
                    <p className="promise-description">We source the freshest local ingredients to ensure every bite is wholesome and delicious.</p>
                </div>
                <div className="promise-card">
                    <span className="promise-icon">❤️</span>
                    <h3 className="promise-title">Made with Love</h3>
                    <p className="promise-description">Our timeless recipes are cooked daily with the care and attention of a home kitchen.</p>
                </div>
                <div className="promise-card">
                    <span className="promise-icon">☀️</span>
                    <h3 className="promise-title">Always Authentic</h3>
                    <p className="promise-description">No compromises. Just pure, traditional flavors that deliver a true homemade taste.</p>
                </div>
            </div>
        </section>
        
        <section id="menu" className="menu-section">
            <div className="section-container text-center">
                <h2 className="section-title">This Week's Menu</h2>
                <p className="section-subtitle">Our menu changes weekly to keep things exciting!</p>
                <div className="menu-grid">
                    <div><h4>Monday</h4><p>Chicken Karahi</p></div>
                    <div><h4>Tuesday</h4><p>Daal Mash</p></div>
                    <div><h4>Wednesday</h4><p>Aloo Qeema</p></div>
                    <div><h4>Thursday</h4><p>Mix Sabzi</p></div>
                    <div><h4>Friday</h4><p>Chicken Pulao</p></div>
                </div>
            </div>
        </section>

        <section id="plans" className="plans-section">
           <div className="section-container text-center">
                <h2 className="section-title">Find Your Perfect Plan</h2>
                <p className="section-subtitle">Affordable plans for individuals and hostels. 5 dinners per week (Mon-Fri).</p>
                <div className="plans-grid">
                    <div className="plan-card">
                        <h3>Basic</h3>
                        <p className="price">Rs. <CountUp target={11000} /><span>/mo</span></p>
                        <ul><li><span>✔</span>Single Main Dish + Roti/Rice</li></ul>
                        <a href="#" className="plan-button">Choose Plan</a>
                    </div>
                    <div className="plan-card featured">
                        <p className="popular-badge">Most Popular</p>
                        <h3>Standard</h3>
                        <p className="price">Rs. <CountUp target={13000} /><span>/mo</span></p>
                        <ul>
                            <li><span>✔</span>Main Dish + Sabzi</li>
                            <li><span>✔</span>Roti, Rice & Salad</li>
                        </ul>
                        <a href="#" className="plan-button featured-button">Choose Plan</a>
                    </div>
                    <div className="plan-card">
                        <h3>Premium</h3>
                        <p className="price">Rs. <CountUp target={15000} /><span>/mo</span></p>
                        <ul>
                            <li><span>✔</span>Full Dinner Spread (2 Dishes)</li>
                            <li><span>✔</span>Roti, Rice, Salad & Raita</li>
                        </ul>
                        <a href="#" className="plan-button">Choose Plan</a>
                    </div>
                </div>
           </div>
        </section>
        {/* --- END OF RESTORED SECTIONS --- */}

     </main>
      <Footer />
    </div>
  );
}

export default HomePage;


